import { createFileRoute } from '@tanstack/react-router'
import { Helmet } from 'react-helmet-async'
import * as s from '@/components/legal-styles'

export const Route = createFileRoute('/legal/cookies')({
  component: CookiePolicyPage,
})

const tableStyle: React.CSSProperties = {
  width: '100%', borderCollapse: 'collapse', marginBottom: 24, fontSize: 14, lineHeight: 1.7,
}
const thStyle: React.CSSProperties = {
  textAlign: 'left', padding: '10px 14px', borderBottom: '2px solid #EDE9E4',
  fontWeight: 700, color: '#1a1216', fontSize: 13,
}
const tdStyle: React.CSSProperties = {
  padding: '10px 14px', borderBottom: '1px solid #EDE9E4', color: '#4a4745', fontSize: 14,
}

function CookiePolicyPage() {
  return (
    <div>
      <Helmet>
        <title>Cookie Policy | One-Email</title>
        <meta name="description" content="One-Email Cookie Policy. We use only essential cookies for authentication. No tracking, no advertising, no analytics cookies." />
        <link rel="canonical" href="https://one-email.com/legal/cookies" />
      </Helmet>

      <h1 style={s.heading}>Cookie Policy</h1>
      <p style={s.updated}>Last updated: March 24, 2026</p>

      <div style={s.highlight}>
        <strong>Summary:</strong> We use only essential cookies for authentication. No tracking, no advertising, no analytics cookies. We show a brief cookie notice on your first visit for transparency, even though our cookies are strictly necessary and legally exempt from consent requirements.
      </div>

      {/* 1. What Are Cookies */}
      <h2 id="what-are-cookies" style={s.h2}>1. What Are Cookies</h2>
      <p style={s.p}>
        Cookies are small text files stored on your device when you visit a website. They serve various purposes &mdash; from keeping you logged in to tracking your behavior across the internet.
      </p>
      <p style={s.p}>
        We believe in minimal cookie usage. We use cookies for one purpose only: keeping you logged in to the dashboard.
      </p>

      {/* 2. Cookies We Use */}
      <h2 id="cookies-we-use" style={s.h2}>2. Cookies We Use</h2>
      <div style={{ overflowX: 'auto', marginBottom: 24 }}>
        <table style={tableStyle}>
          <thead>
            <tr>
              <th style={thStyle}>Cookie</th>
              <th style={thStyle}>Type</th>
              <th style={thStyle}>Provider</th>
              <th style={thStyle}>Purpose</th>
              <th style={thStyle}>Duration</th>
              <th style={thStyle}>Attributes</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={tdStyle}>Session cookie (<code>oe_*</code>)</td>
              <td style={tdStyle}>Strictly necessary</td>
              <td style={tdStyle}>One-Email (first-party)</td>
              <td style={tdStyle}>Maintains your authenticated login session on the dashboard</td>
              <td style={tdStyle}>7 days, refreshed after 1 day of activity</td>
              <td style={tdStyle}><code>httpOnly</code>, <code>Secure</code>, <code>SameSite=None</code></td>
            </tr>
          </tbody>
        </table>
      </div>
      <p style={s.p}>That is the complete list. We use <strong>one</strong> cookie.</p>

      {/* 3. Cookies We Do NOT Use */}
      <h2 id="cookies-we-dont-use" style={s.h2}>3. Cookies We Do NOT Use</h2>
      <ul style={s.ul}>
        <li style={s.li}><strong>Tracking cookies</strong> &mdash; We do not track your behavior across websites.</li>
        <li style={s.li}><strong>Advertising cookies</strong> &mdash; We do not serve ads or participate in ad networks.</li>
        <li style={s.li}><strong>Analytics cookies</strong> &mdash; We do not use Google Analytics, Mixpanel, PostHog, Plausible, or any analytics service on the dashboard or marketing site.</li>
        <li style={s.li}><strong>Third-party cookies</strong> &mdash; No third-party service sets cookies through our site.</li>
        <li style={s.li}><strong>Social media cookies</strong> &mdash; We do not embed social media widgets that set cookies.</li>
      </ul>

      {/* 4. Our Cookie Banner */}
      <h2 id="cookie-banner" style={s.h2}>4. Our Cookie Banner</h2>
      <p style={s.p}>When you first visit One-Email, you&apos;ll see a small banner that says:</p>
      <div style={s.highlight}>
        <strong>We only use essential cookies for authentication. No tracking, no ads, no analytics.</strong> [OK] [<a href="/legal/cookies">Learn more</a>]
      </div>
      <p style={s.p}>
        We show this for transparency &mdash; not because we&apos;re legally required to. Under the ePrivacy Directive (Article 5(3)), cookies that are strictly necessary to provide a service you explicitly requested are exempt from the consent requirement. Our session cookie exists solely to maintain your authenticated login, so no consent is legally needed and there is nothing to opt out of.
      </p>
      <p style={s.p}>We believe telling you upfront is the right thing to do.</p>
      <p style={s.p}>
        If we add non-essential cookies in the future (for example, analytics), we will update this policy and implement full consent mechanisms before doing so.
      </p>

      {/* 5. How to Control Cookies */}
      <h2 id="control" style={s.h2}>5. How to Control Cookies</h2>
      <p style={s.p}>
        You can delete or block cookies through your browser settings. However, if you block the session cookie, you will not be able to stay logged in to the One-Email dashboard.
      </p>
      <ul style={s.ul}>
        <li style={s.li}><a href="https://support.google.com/chrome/answer/95647" target="_blank" rel="noopener noreferrer">Chrome</a></li>
        <li style={s.li}><a href="https://support.mozilla.org/en-US/kb/cookies-information-websites-store-on-your-computer" target="_blank" rel="noopener noreferrer">Firefox</a></li>
        <li style={s.li}><a href="https://support.apple.com/guide/safari/manage-cookies-sfri11471/mac" target="_blank" rel="noopener noreferrer">Safari</a></li>
        <li style={s.li}><a href="https://support.microsoft.com/en-us/microsoft-edge/manage-cookies-in-microsoft-edge-63947406-40ac-c3b8-57b9-2a946a29ae09" target="_blank" rel="noopener noreferrer">Edge</a></li>
      </ul>

      {/* 6. Changes */}
      <h2 id="changes" style={s.h2}>6. Changes</h2>
      <p style={s.p}>
        If we add new cookies, we will update this policy and notify you via email or dashboard notification before the change takes effect.
      </p>

      {/* 7. Contact */}
      <h2 id="contact" style={s.h2}>7. Contact</h2>
      <p style={s.p}>Questions about cookies? Email privacy@one-email.com.</p>
    </div>
  )
}
